/**
 * The unread count, for the application shell.
 *
 * Exported as a component rather than injected into `core-ui`'s `AppShell`,
 * because the shell is a `core-*` package and must not know that notifications
 * exist. The application composes the two — which is the same reason
 * `apps/web` is described as a composition root rather than a place where
 * things are implemented.
 *
 * It polls. Not because polling is good, but because the alternatives are a
 * WebSocket or server-sent events, and both mean a connection to hold open,
 * to authenticate, to reconnect and to scale — infrastructure a template
 * should not commit a fork to for a number in a corner. The interval is
 * deliberately slow; a notification arriving a minute late is not a failure.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "@hardauto/core-api";

import { NotificationsApi } from "./api";
import { NAMESPACE } from "./i18n";
import "./screens/notifications.css";

export interface NotificationBellProps {
  client: ApiClient;
  /** Where the bell navigates. Supplied so this package does not import a router. */
  href: string;
  /** How often to re-check, in milliseconds. */
  pollMs?: number;
}

export const DEFAULT_POLL_MS = 60_000;

export function NotificationBell({
  client,
  href,
  pollMs = DEFAULT_POLL_MS,
}: NotificationBellProps) {
  const { t } = useTranslation(NAMESPACE);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    try {
      // `limit: 1` — this call wants the count, not the notifications, and
      // asking for thirty rows a minute to read one number is a cost paid by
      // every signed-in tab.
      const page = await new NotificationsApi(client).list({ unread_only: true, limit: 1 });
      setUnread(page.unread);
    } catch {
      // Silent. A failed count is not worth a message: the number simply does
      // not change, and the next poll will correct it. A toast here would fire
      // every minute for as long as the network was unhappy.
    }
  }, [client]);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => {
      void refresh();
    }, pollMs);
    return () => {
      clearInterval(timer);
    };
  }, [refresh, pollMs]);

  return (
    <a
      className="hardauto-bell"
      href={href}
      // The count is IN the accessible name, not only in a badge beside the
      // icon. A screen-reader user otherwise hears "Notifications" and has no
      // way to know whether there is anything to read.
      aria-label={unread > 0 ? t("bell.labelWithUnread", { count: unread }) : t("bell.label")}
    >
      <span aria-hidden="true">🔔</span>
      {unread > 0 ? (
        <span className="hardauto-bell__count" aria-hidden="true">
          {unread > 99 ? "99+" : unread}
        </span>
      ) : null}
    </a>
  );
}
